/**
 * Shared helpers for file download operations
 * Used by Files and FilesNotary factories to avoid code duplication
 */

import type { IBaseApiService } from "@Front/Api/BaseApiServiceFactory";
import {
	buildDownloadAggregatedCertificatePayload,
	buildDownloadMultipleFilesPayload,
} from "./fileRequestPayloadHelpers";

export interface DownloadMultipleFilesOptions {
	fileUids: string[];
	includeAnchors?: boolean;
	folderNumber?: string;
	folderName?: string;
}

/**
 * Helper to download multiple files as a ZIP archive
 * @param baseApi - Base API service instance (IBaseNotary or IBaseCustomer)
 * @param baseUrl - Base URL for the endpoint (e.g., "/notary/files" or "/customer/files-notary")
 * @param options - fileUids, optional includeAnchors (default true), folderNumber, folderName
 * @returns Promise resolving to a Blob containing the ZIP archive
 */
export async function downloadMultipleFiles(
	baseApi: IBaseApiService,
	baseUrl: string,
	options: DownloadMultipleFilesOptions,
): Promise<Blob> {
	const { fileUids, includeAnchors = true, folderNumber, folderName } = options;
	const url = new URL(baseUrl.concat("/download-multiple"));
	try {
		const payload = buildDownloadMultipleFilesPayload(fileUids, includeAnchors, folderNumber, folderName);
		return await baseApi.postRequest<Blob>(url, payload);
	} catch (err) {
		baseApi.onError(err);
		return Promise.reject(err);
	}
}

/**
 * Helper to download aggregated certificate for multiple documents
 * @param baseApi - Base API service instance (IBaseNotary or IBaseCustomer)
 * @param baseUrl - Base URL for the endpoint (e.g., "/notary/files" or "/customer/files")
 * @param documentUids - Array of document UIDs
 * @param folderUid - Folder UID
 * @returns Promise resolving to a Blob containing the certificate PDF
 */
export async function downloadAggregatedCertificate(
	baseApi: IBaseApiService,
	baseUrl: string,
	documentUids: string[],
	folderUid: string,
): Promise<Blob> {
	const url = new URL(baseUrl.concat("/download-aggregated-certificate"));
	try {
		const payload = buildDownloadAggregatedCertificatePayload(documentUids, folderUid);
		return await baseApi.postRequest<Blob>(url, payload);
	} catch (err) {
		baseApi.onError(err);
		return Promise.reject(err);
	}
}
